/**
 * One poll, many subscribers (PD-16055).
 *
 * The TopBar renders its desktop controls AND its mobile bottom bar at the
 * same time — only CSS decides which is visible — so shell indicators like
 * the notifications bell and the communications dot exist twice in the tree.
 * When each copy owns its own `setInterval`, the tenant pays double for
 * every badge, forever, on every open tab.
 *
 * A shared poll gives the value a single owner instead. The first subscriber
 * starts the timer and triggers the first fetch; later subscribers receive
 * the value already in hand and join the existing cadence without issuing a
 * request of their own; the last unsubscribe stops the timer. `reset()`
 * clears the value for a new session.
 *
 * Deliberately not react-query: these are plain interval polls with no cache
 * keys, no pagination and no invalidation graph, and the hooks that read them
 * already have their shape. This keeps the change at the ownership of the
 * timer, which is the actual defect.
 */

export interface SharedPoll<T> {
	/** Subscribe to the value. Returns an unsubscribe function. */
	subscribe: (listener: (value: T) => void) => () => void;
	/** Current value without subscribing. */
	getSnapshot: () => T;
	/** Fetch now, out of band — used after an action that changes the count. */
	refresh: () => Promise<void>;
	/** Drop the value and stop polling (logout, tenant switch). */
	reset: () => void;
}

export function createSharedPoll<T>(
	fetcher: () => Promise<T>,
	intervalMs: number,
	initialValue: T,
): SharedPoll<T> {
	let value = initialValue;
	let timer: ReturnType<typeof setInterval> | null = null;
	let inFlight: Promise<void> | null = null;
	const listeners = new Set<(value: T) => void>();

	const emit = (): void => {
		for (const listener of listeners) {
			listener(value);
		}
	};

	const refresh = async (): Promise<void> => {
		// Collapse concurrent refreshes — two subscribers mounting in the same
		// tick must not produce two requests.
		if (inFlight != null) {
			return inFlight;
		}
		inFlight = (async (): Promise<void> => {
			try {
				const next = await fetcher();
				value = next;
				emit();
			} catch {
				// Non-fatal — keep the last known value rather than zeroing a
				// badge because one poll failed.
			} finally {
				inFlight = null;
			}
		})();
		return inFlight;
	};

	return {
		subscribe: (listener): (() => void) => {
			listeners.add(listener);
			// Hand the new subscriber whatever is already known, so a second
			// copy of a badge paints immediately instead of flashing zero.
			listener(value);
			if (timer == null) {
				void refresh();
				timer = setInterval(() => {
					void refresh();
				}, intervalMs);
			}
			return (): void => {
				listeners.delete(listener);
				if (listeners.size === 0 && timer != null) {
					clearInterval(timer);
					timer = null;
				}
			};
		},
		getSnapshot: (): T => value,
		refresh,
		reset: (): void => {
			value = initialValue;
			if (timer != null) {
				clearInterval(timer);
				timer = null;
			}
			emit();
		},
	};
}
